<template>
  <v-card>
    <v-card-title class="py-2">Matriz de Producción por Máquina</v-card-title>
    <v-divider />

    <v-card-text class="pa-0">
      <div class="scroll-x">
        <v-simple-table dense class="matriz-table">
          <thead>
            <tr>
              <th class="sticky-top sticky-left-1" rowspan="2">Máquina</th>
              <th class="sticky-top sticky-left-2" rowspan="2">Métrica</th>

              <th v-for="g in GetGruposDia()" :key="g.key" class="sticky-top text-center" :colspan="g.columnas.length">
                {{ g.label }}
              </th>
            </tr>

            <tr>
              <th v-for="c in GetColumnas()" :key="c.key" class="sticky-top text-center">
                {{ c.label }}
              </th>
            </tr>
          </thead>

          <tbody>
            <template v-for="row in GetFilasRender()">
              <tr>
                <td v-if="row.mostrarMaquina" class="sticky-left-1 font-weight-medium" :rowspan="row.rowspan">
                  {{ row.maquinaNombre }}
                </td>

                <td class="sticky-left-2">{{ row.metricaLabel }}</td>

                <td v-for="c in GetColumnas()" :key="c.key" class="text-right">
                  {{ FormatearValor(row.valoresPorColumna[c.key], row.tipo) }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-simple-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "MatrizProduccionPorMaquina",

  props: {
    // TU JSON, tal cual
    maquinas: { type: Array, default: () => [] },
  },

  data() {
    return {
      incluirTotalDia: true,

      metricas: [
        { key: "horas", label: "Horas Laboradas", tipo: "horas" },
        { key: "planeadas", label: "Pza Planeadas", tipo: "int" },
        { key: "hechas", label: "Pza Realizadas", tipo: "int" },
        { key: "eficiencia", label: "Eficiencia", tipo: "pct" },
      ],

      gruposDia: [],
      columnas: [],
      filasRender: [],

      _lastSig: "",
      _building: false,
    };
  },

  methods: {
    // ====== Getters para template (sin computed) ======
    GetGruposDia() {
      this.EnsureCache();
      return this.gruposDia || [];
    },

    GetColumnas() {
      this.EnsureCache();
      return this.columnas || [];
    },

    GetFilasRender() {
      this.EnsureCache();
      return this.filasRender || [];
    },

    EnsureCache() {
      if (this._building) return;

      const sig = this.FirmaMaquinas(this.maquinas);
      if (sig === this._lastSig) return;

      this._lastSig = sig;

      this._building = true;
      try {
        this.RebuildCache(this.maquinas);
      } finally {
        this._building = false;
      }
    },

    FirmaMaquinas(maquinas) {
      const arr = Array.isArray(maquinas) ? maquinas : [];
      const n = arr.length;
      if (n === 0) return "M:0";

      const firstId = arr[0]?.idMaquinaIns ?? "";
      const lastId = arr[n - 1]?.idMaquinaIns ?? "";

      let countRangos = 0;
      let sumSample = 0;

      for (let i = 0; i < Math.min(3, n); i += 1) {
        const r = Array.isArray(arr[i]?.esperadasPorRango) ? arr[i].esperadasPorRango : [];
        countRangos += r.length;

        for (let k = 0; k < Math.min(5, r.length); k += 1) {
          sumSample += Number(r[k]?.esperadasReales ?? 0);
          sumSample += Number(r[k]?.hechasReales ?? 0);
        }
      }

      return `M:${n}:${firstId}:${lastId}:${countRangos}:${sumSample}`;
    },

    // ====== Construcción dinámica a partir de TU JSON ======
    RebuildCache(maquinas) {
      const maquinasArr = Array.isArray(maquinas) ? maquinas : [];

      // 1) Días y turnos detectados por "inicio"
      const diasMap = {}; // { [YYYY-MM-DD]: { key,label, turnosInfo:{[idTurno]:{inicioTime, cruza}} } }

      for (let mi = 0; mi < maquinasArr.length; mi += 1) {
        const rangos = Array.isArray(maquinasArr[mi]?.esperadasPorRango) ? maquinasArr[mi].esperadasPorRango : [];
        for (let ri = 0; ri < rangos.length; ri += 1) {
          const r = rangos[ri];
          const inicio = String(r?.inicio || "");
          const fin = String(r?.fin || "");
          const dia = inicio.slice(0, 10);
          if (!dia) continue;

          if (!diasMap[dia]) {
            diasMap[dia] = { key: dia, label: this.LabelDia(dia), turnosInfo: {} };
          }

          const idTurno = r?.idTurno;
          if (!idTurno) continue;

          if (!diasMap[dia].turnosInfo[idTurno]) {
            const inicioTime = (inicio.split("T")[1] || "00:00:00");
            const cruza = inicio.slice(0, 10) !== fin.slice(0, 10);

            const turno = String(r?.turno || "").trim(); // <- NUEVO

            diasMap[dia].turnosInfo[idTurno] = {
              inicioTime,
              cruza,
              turno: turno || `T${idTurno}`, // <- NUEVO
            };
          }
        }
      }

      const dias = Object.values(diasMap);
      dias.sort((a, b) => String(a.key).localeCompare(String(b.key)));

      // 2) Grupos/Columnas (encabezado día + turnos)
      const grupos = [];
      const columnas = [];

      for (let di = 0; di < dias.length; di += 1) {
        const d = dias[di];

        const turnos = Object.keys(d.turnosInfo)
          .map(x => Number(x))
          .filter(x => !Number.isNaN(x));

        turnos.sort((a, b) => {
          const ta = d.turnosInfo[a]?.inicioTime || "00:00:00";
          const tb = d.turnosInfo[b]?.inicioTime || "00:00:00";
          return ta.localeCompare(tb);
        });

        const colsDia = [];
        if (this.incluirTotalDia) {
          colsDia.push({ key: `${d.key}|TOTAL`, label: "Día", isTotal: true });
        }

        for (let ti = 0; ti < turnos.length; ti += 1) {
          const idTurno = turnos[ti];
          const cruza = Boolean(d.turnosInfo[idTurno]?.cruza);

          const nombreTurno = d.turnosInfo[idTurno]?.turno || `T${idTurno}`; // <- AQUÍ

          colsDia.push({
            key: `${d.key}|${idTurno}`,
            label: cruza ? `${nombreTurno} (→ día sig.)` : `${nombreTurno}`,
            isTotal: false,
            idTurno,
            diaKey: d.key,
          });
        }

        grupos.push({ key: d.key, label: d.label, columnas: colsDia });

        for (let ci = 0; ci < colsDia.length; ci += 1) columnas.push(colsDia[ci]);
      }

      // 3) Index por máquina/día/turno desde TU JSON
      const idxMaquinas = [];
      for (let mi = 0; mi < maquinasArr.length; mi += 1) {
        const m = maquinasArr[mi];
        const id = m?.idMaquinaIns;
        if (!id) continue;

        const entry = {
          idMaquinaIns: id,
          nombre: this.NombreMaquina(m),
          porDia: {},
        };

        const rangos = Array.isArray(m?.esperadasPorRango) ? m.esperadasPorRango : [];
        for (let ri = 0; ri < rangos.length; ri += 1) {
          const r = rangos[ri];
          const inicio = String(r?.inicio || "");
          const fin = String(r?.fin || "");
          const dia = inicio.slice(0, 10);
          const idTurno = r?.idTurno;

          if (!dia || !idTurno) continue;
          if (!entry.porDia[dia]) entry.porDia[dia] = {};

          const planeadas = Number(r?.esperadasReales ?? 0);
          const hechas = Number(r?.hechasReales ?? 0);

          // No tienes horas reales en el JSON; uso duración del rango solo si hay actividad
          const horas = (planeadas > 0 || hechas > 0) ? this.DuracionHoras(inicio, fin) : 0;

          entry.porDia[dia][idTurno] = { horas, planeadas, hechas };
        }

        idxMaquinas.push(entry);
      }

      idxMaquinas.sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")));

      // 4) Filas
      const filas = [];
      for (let mi = 0; mi < idxMaquinas.length; mi += 1) {
        const m = idxMaquinas[mi];

        for (let k = 0; k < this.metricas.length; k += 1) {
          const met = this.metricas[k];
          const valores = {};

          for (let g = 0; g < grupos.length; g += 1) {
            const diaKey = grupos[g].key;

            for (let c = 0; c < grupos[g].columnas.length; c += 1) {
              const col = grupos[g].columnas[c];

              if (col.isTotal) {
                const tot = this.TotalesMaquinaDia(m, diaKey);
                valores[col.key] = this.ValorPorMetrica(tot, met.key);
              } else {
                const det = this.DetalleMaquinaDiaTurno(m, col.diaKey, col.idTurno);
                valores[col.key] = this.ValorPorMetrica(det, met.key);
              }
            }
          }

          filas.push({
            key: `${m.idMaquinaIns}-${met.key}`,
            maquinaNombre: m.nombre,
            mostrarMaquina: k === 0,
            rowspan: this.metricas.length,
            metricaLabel: met.label,
            tipo: met.tipo,
            valoresPorColumna: valores,
          });
        }
      }

      // SETS (sin _cache)
      this.gruposDia = grupos;
      this.columnas = columnas;
      this.filasRender = filas;
    },

    // ===== Helpers =====
    LabelDia(fechaYYYYMMDD) {
      const f = String(fechaYYYYMMDD || "").trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(f)) return f || "Día";
      const d = new Date(`${f}T00:00:00`);
      const wd = d.toLocaleDateString("es-MX", { weekday: "long" });
      return `[${wd}] ${f}`;
    },

    NombreMaquina(m) {
      const n = String(m?.nombre || "").trim();
      const mod = String(m?.modelo || "").trim();
      if (n && mod) return `[${n}] - ${mod}`;
      return n || mod || `Máquina ${m?.idMaquinaIns ?? ""}`;
    },

    DuracionHoras(inicioIso, finIso) {
      const a = Date.parse(inicioIso);
      const b = Date.parse(finIso);
      if (!Number.isNaN(a) && !Number.isNaN(b)) {
        const h = (b - a) / 3600000;
        return h > 0 ? h : 0;
      }
      return 0;
    },

    DetalleMaquinaDiaTurno(m, diaKey, idTurno) {
      const det = m?.porDia?.[diaKey]?.[idTurno] || null;
      return det || { horas: 0, planeadas: 0, hechas: 0 };
    },

    TotalesMaquinaDia(m, diaKey) {
      const mapa = m?.porDia?.[diaKey] || {};
      const turnos = Object.values(mapa);

      let horas = 0;
      let planeadas = 0;
      let hechas = 0;

      for (let i = 0; i < turnos.length; i += 1) {
        horas += Number(turnos[i]?.horas ?? 0);
        planeadas += Number(turnos[i]?.planeadas ?? 0);
        hechas += Number(turnos[i]?.hechas ?? 0);
      }

      return { horas, planeadas, hechas };
    },

    ValorPorMetrica(obj, key) {
      const horas = Number(obj?.horas ?? 0);
      const planeadas = Number(obj?.planeadas ?? 0);
      const hechas = Number(obj?.hechas ?? 0);

      if (key === "horas") return horas;
      if (key === "planeadas") return planeadas;
      if (key === "hechas") return hechas;
      if (key === "eficiencia") return planeadas > 0 ? (hechas / planeadas) * 100 : 0;
      return 0;
    },

    FormatearValor(val, tipo) {
      const n = Number(val ?? 0);
      if (tipo === "pct") return `${n.toFixed(2)} %`;
      if (tipo === "horas") return n.toFixed(2);
      if (tipo === "int") return n.toLocaleString("es-MX");
      return String(val ?? "0");
    },
  },
};
</script>

<style scoped>
.scroll-x {
  overflow-x: auto;
}

.matriz-table th,
.matriz-table td {
  white-space: nowrap;
}

.sticky-top {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 5;
}

.sticky-left-1 {
  position: sticky;
  left: 0;
  min-width: 360px;
  background: #fff;
  z-index: 6;
}

.sticky-left-2 {
  position: sticky;
  left: 360px;
  min-width: 180px;
  background: #fff;
  z-index: 6;
}
</style>
